import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import {
  useCustomerLevelVariables,
  usePostponeReadVariables,
} from '@bingads-webui-campaign-react/basic-context-utils';
import { useCurrencies } from '@bingads-webui/overreact-cc';
import { useCurrencyCountryMapping } from '@bingads-webui-campaign-react/use-currency-country-mapping';
import { usePromisify } from '@bingads-webui-react/overreact';

import { CurrencyDataContext } from './currency-data-context';

function CurrencyDataProviderInt({ updateValue }) {
  const customerLevelVariables = useCustomerLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();

  const {
    getCurrenciesForCountry,
    getDefaultCurrencyForCountry,
    isUseCurrencyCountryMappingReady,
  } = useCurrencyCountryMapping({ customerLevelVariables });

  const [, { read: readCurrenciesFn }] = useCurrencies(customerLevelVariables, postponeReadConfig);
  const [readCurrenciesAsync] = usePromisify([readCurrenciesFn]);
  const readCurrencies = useCallback((args = {}, ...rest) => readCurrenciesAsync(args, ...rest), [readCurrenciesAsync]);

  const value = useMemo(() => ({
    getCurrenciesForCountry,
    getDefaultCurrencyForCountry,
    isUseCurrencyCountryMappingReady,
    readCurrencies,
  }), [getCurrenciesForCountry, getDefaultCurrencyForCountry, isUseCurrencyCountryMappingReady, readCurrencies]);

  useEffect(() => {
    updateValue(value);
  }, [updateValue, value]);

  useEffect(() => {
    readCurrencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

CurrencyDataProviderInt.propTypes = {
  updateValue: PropTypes.func.isRequired,
};

export function CurrencyDataProvider({ children }) {
  const { scope } = useContext(ScopeContext);
  const { aid } = scope.values;
  const [value, updateValue] = useState({});

  return (
    <CurrencyDataContext.Provider value={value}>
      {children}
      {aid && (
        <CurrencyDataProviderInt updateValue={updateValue} />
      )}
    </CurrencyDataContext.Provider>
  );
}

CurrencyDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
